/**
 * Stego encoder — orchestrates key derivation, encryption, header packing, and blob assembly.
 *
 * Blob layout: salt(16) + header(77) + ciphertext(N)
 * This matches the Python encoder exactly.
 */

import { deriveKey } from "../crypto/argon2.js";
import { encryptPayload } from "../crypto/aes-gcm.js";
import { concat } from "../crypto/utils.js";
import {
  HEADER_VERSION,
  Technique,
  packHeader,
  type StegoHeader,
} from "./header.js";

export interface EncodeOptions {
  technique: Technique;
  isFile?: boolean;
}

export interface EncodeResult {
  /** Full blob: salt + header + ciphertext */
  blob: Uint8Array;
  header: StegoHeader;
}

/**
 * Encode a payload: derive key, encrypt, pack header, assemble blob.
 * The blob is ready to be embedded into a carrier via a technique.
 */
export async function encodePayload(
  payload: Uint8Array,
  passphrase: string,
  opts: EncodeOptions
): Promise<EncodeResult> {
  // Step 1: Derive key (generates random salt)
  const derived = await deriveKey(passphrase);

  // Step 2: Encrypt (compress + AES-GCM)
  const encrypted = await encryptPayload(derived.key, payload);

  // Step 3: Build header
  const header: StegoHeader = {
    version: HEADER_VERSION,
    technique: opts.technique,
    isChunked: false,
    isZip: false,
    isFile: opts.isFile ?? false,
    chunkIndex: 0,
    totalChunks: 1,
    originalSize: payload.length,
    chunkSize: encrypted.compressedSize,
    payloadSha256: encrypted.originalHash,
    nonce: encrypted.nonce,
    authTag: encrypted.authTag,
  };

  const headerBytes = packHeader(header);

  // Step 4: Assemble blob — salt(16) + header(77) + ciphertext
  const blob = concat(derived.salt, headerBytes, encrypted.ciphertext);

  return { blob, header };
}
